import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, computed, effect, inject, OnInit, PLATFORM_ID, Signal, ViewChild } from '@angular/core';
import { MatCardContent, MatCardModule } from "@angular/material/card";
import { SalesService } from '../../services/sales-agent.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, switchMap, tap } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { SalesIdCard } from '../sales-id-card/sales-id-card';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { CountUpDirective } from '../../../../shared/general/directives/count-up.directive';
import { GtmService } from '../../../../core/services/gtm.service';
import { AgentW9RecordComponent } from "../agent-w9-records/agent-w9-records";
import { AgentFormRecordComponent } from '../agent-form-records/agent-form-records';
import { SeoService } from '../../../../shared/services/seo.service';
import { ReferralUrlComponent } from '../referral-url/referral-url';
import { SalesAgent } from '../../models/sales-agent.model';
import { ReferralUrlService } from '../../services/referral-url.service';
import { PhonePipe } from '../../../../shared/pipes/phone-number.pipe';

@Component({
  selector: 'app-sales-dashboard',
  imports: [
    CommonModule,
    MatCardModule,
    MatCardContent,
    SalesIdCard,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    CountUpDirective,
    AgentFormRecordComponent,
    AgentW9RecordComponent,
    ReferralUrlComponent,
    PhonePipe
],
  templateUrl: './sales-dashboard.html',
  styleUrl: './sales-dashboard.scss',
})
export class SalesDashboard implements OnInit {
  private salesService = inject(SalesService);
  private authService = inject(AuthService);
  private referralUrlService = inject(ReferralUrlService);
  private gtm = inject(GtmService);
  private seo = inject(SeoService);
  platformId = inject(PLATFORM_ID);
  referralUrlClicks = 0;
  displayedColumns: string[] = ['host', 'address', 'city', 'state', 'expires'];
  referredColumns: string[] = ['name', 'agentId', 'phone', 'email', 'location'];
  referredDataSource = new MatTableDataSource<SalesAgent>();

  @ViewChild(MatPaginator) set referredPaginator(mp: MatPaginator) {
    this.referredDataSource.paginator = mp;
  }

  @ViewChild(MatSort) set referredSort(ms: MatSort) {
    this.referredDataSource.sort = ms;
  }
  // 1. Convert the Auth User to a Signal
  // this.user() will automatically update whenever the observable emits
  private user = toSignal(this.authService.user$);

  // 2. Derive the Agent ID from the user signal
  // This will be undefined until the user logs in
  agentUserId = computed(() => this.user()?.id);

  // 3. Automatically re-fetch data whenever agentUserId changes
  data: Signal<any> = toSignal(
    toObservable(this.agentUserId).pipe(
      // filter out undefined/null if you don't want to call the API without an ID
      filter(id => !!id),
      switchMap(id => this.salesService.getSalesDashboard(id!)),
      tap(data => console.log('Sales Dashboard Data:', data))
    )
  );

  referred: Signal<any[] | undefined> = toSignal(
    toObservable(this.agentUserId).pipe(
      // 1. Wait for the authenticated User ID
      filter(id => !!id),
      
      // 2. Fetch the Sales Agent profile based on User ID
      switchMap(userId => this.salesService.getSalesAgentByUserId(userId!)),
      
      // 3. Ensure the agent profile exists and has the required agentId
      // Note: We use 'agentId' here as per your specific requirement
      filter(agent => !!agent?.agentId),
      
      // 4. Cast to Number and fetch the list of referred agents
      switchMap(agent => {
        const numericId = Number(agent.agentId);
        return this.salesService.getSalesAgentReferredAgents(numericId);
      })
    )
  );

  private readonly syncReferredDataSource = effect(() => {
    const agents = this.referred();
    if (agents) {
      this.referredDataSource.data = agents;
    }
  });

  constructor() {
    this.referredDataSource.sortingDataAccessor = (agent, property) => {
      switch (property) {
        case 'name':
          return `${agent.firstName ?? ''}${agent.lastName ?? ''}`.toLowerCase();
        case 'phone':
          return agent.phoneNumber ?? '';
        default:
          return (agent as any)[property] ?? '';
      }
    };
  }

  ngOnInit(): void {
    this.seo.updateMetaTags('Sales Dashboard', 'View of sales agent data on Horizinns');
    if (isPlatformBrowser(this.platformId)) {
      this.gtm.pageView({
        page_path: '/sales/dashboard',
        page_title: 'Sales Dashboard | Horizinns'
      });

      this.setReferralUrlClicks();
    }
  }

  setReferralUrlClicks() {
    const userId = this.authService.currentUser()?.id;
    if (userId) {
      this.salesService.getSalesAgentByUserId(userId).pipe(
        switchMap((agent: SalesAgent) => {
          return this.referralUrlService.getReferralUrlClicks(agent.agentId);
        }),
        tap((clicks: number) => {
          this.referralUrlClicks = clicks;
        })).subscribe();
    }
  }
}
